import { InvoiceHistoryResponse } from "../typings/interfaces/responses/invoice-history-response";

// The discount a bill was created with, from its CREATED event's snapshot
// (session.discount in billString, see payment-modal's getAppliedDiscount).
// Null when it had none, or only a dormant $0 one. Bills saved before the
// details were kept show as "Discount" with no % / $ value.
export function describeSnapshotDiscount(
  originalBillSnapshot: string
): { name: string; value: string | null; amount: number } | null {
  try {
    const saved = (JSON.parse(originalBillSnapshot) as { discount?: any }).discount;
    const amount = Number(saved?.amount ?? 0);
    if (!(amount > 0)) return null;
    const details = saved?.details;
    const value = details
      ? details.discountMode === "FIXED"
        ? `$${Number(details.discountAmount).toFixed(2)}`
        : `${details.discountAmount}%`
      : null;
    return { name: details?.discountName || "Discount", value, amount };
  } catch {
    return null;
  }
}

// A bill created with a discount shows as two steps: "Order Created" at the
// price before the discount, then "Discount Applied" with the discount and
// the new total. The CREATED event's amount/balance are after the discount
// (BillServiceImpl), so the discount from its snapshot is added back.
export function withCheckInDiscountEvent(
  events: InvoiceHistoryResponse[]
): InvoiceHistoryResponse[] {
  const index = events.findIndex(
    (event) => event.title === "Order Created" && event.metadata?.originalBillSnapshot
  );
  if (index < 0) return events;
  const created = events[index];
  const discount = describeSnapshotDiscount(created.metadata!.originalBillSnapshot!);
  if (!discount) return events;

  const totalAfter = created.amount ?? 0;
  const totalBefore = totalAfter + discount.amount;
  const createdBefore: InvoiceHistoryResponse = {
    ...created,
    amount: created.amount != null ? totalBefore : created.amount,
    balance: created.balance != null ? created.balance + discount.amount : created.balance,
  };
  const discountEvent: InvoiceHistoryResponse = {
    ...created,
    id: -created.id,
    type: "DISCOUNT_APPLIED",
    title: "Discount Applied",
    amount: null,
    pieces: null,
    previousPieces: null,
    checkNumber: null,
    paymentStatusLabel: null,
    description: "",
    metadata: {
      discountApplied: { ...discount, totalBefore, totalAfter },
    },
  };
  // Oldest-first timeline: the discount goes right after the creation.
  const ascending =
    events.length < 2 ||
    new Date(events[0].createdAt).getTime() <=
      new Date(events[events.length - 1].createdAt).getTime();
  const next = [...events];
  next.splice(index, 1, ...(ascending ? [createdBefore, discountEvent] : [discountEvent, createdBefore]));
  return next;
}
